import React, { useState, useEffect } from 'react';
import { Bell, CheckCheck, Calendar, RefreshCw, XCircle, Edit3 } from 'lucide-react';
import api from '../../api';
import { format } from 'date-fns';

const kindIcons = {
    new_submission: <Calendar size={16} style={{ color: '#6366f1' }} />,
    resubmitted: <RefreshCw size={16} style={{ color: '#f59e0b' }} />,
    approved: <CheckCheck size={16} style={{ color: '#10b981' }} />,
    rejected: <XCircle size={16} style={{ color: '#ef4444' }} />,
    needs_edits: <Edit3 size={16} style={{ color: '#f59e0b' }} />,
};

export default function AdminNotifications() {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            setLoading(true);
            const data = await api.getAdminNotifications();
            setNotifications(data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleRead = async (id) => {
        await api.markAdminNotificationRead(id).catch(() => { });
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    };

    const handleReadAll = async () => {
        await api.markAllAdminNotificationsRead().catch(() => { });
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    };

    const unread = notifications.filter(n => !n.read).length;

    if (loading) return (
        <div className="admin-card-premium" style={{ padding: '48px', textAlign: 'center' }}>
            <p style={{ color: 'var(--admin-text-muted)' }}>Loading notifications...</p>
        </div>
    );

    return (
        <div>
            <div className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">Notifications</h1>
                    {unread > 0 && (
                        <p style={{ color: 'var(--admin-text-muted)', marginTop: 4 }}>
                            {unread} unread notification{unread !== 1 ? 's' : ''}
                        </p>
                    )}
                </div>
                {notifications.length > 0 && (
                    <button className="btn-premium btn-premium-secondary" onClick={handleReadAll}>
                        <CheckCheck size={16} />
                        Mark All Read
                    </button>
                )}
            </div>

            {notifications.length === 0 ? (
                <div className="admin-card-premium" style={{ padding: '64px', textAlign: 'center' }}>
                    <Bell size={48} style={{ color: 'var(--admin-text-muted)', margin: '0 auto 16px', display: 'block', opacity: 0.4 }} />
                    <h3 style={{ marginBottom: 8, color: 'var(--admin-text-main)' }}>All Caught Up</h3>
                    <p style={{ color: 'var(--admin-text-muted)' }}>No notifications yet. Partner submissions will appear here.</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {notifications.map(n => (
                        <div
                            key={n.id}
                            className="admin-card-premium"
                            style={{
                                padding: '18px 24px',
                                display: 'flex',
                                gap: 16,
                                alignItems: 'flex-start',
                                borderLeft: n.read ? '3px solid transparent' : '3px solid var(--admin-primary)',
                                opacity: n.read ? 0.7 : 1,
                                transition: 'all 0.2s',
                            }}
                        >
                            <div style={{ marginTop: 2, flexShrink: 0 }}>
                                {kindIcons[n.kind] || <Bell size={16} style={{ color: 'var(--admin-primary)' }} />}
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--admin-text-main)' }}>{n.title}</div>
                                <div style={{ fontSize: '0.9rem', color: 'var(--admin-text-muted)' }}>{n.message}</div>
                                {n.created_at && (
                                    <div style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)', marginTop: 6 }}>
                                        {format(new Date(n.created_at), 'MMM d, yyyy • h:mm a')}
                                    </div>
                                )}
                            </div>
                            {!n.read && (
                                <button
                                    onClick={() => handleRead(n.id)}
                                    style={{
                                        fontSize: '0.8rem',
                                        color: 'var(--admin-primary)',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        flexShrink: 0,
                                        padding: '4px 8px',
                                        borderRadius: 6,
                                        transition: 'background 0.2s',
                                    }}
                                    onMouseOver={e => e.currentTarget.style.background = 'rgba(79,70,229,0.1)'}
                                    onMouseOut={e => e.currentTarget.style.background = 'none'}
                                >
                                    Mark read
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
